'use client';

import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { useGetUser } from '@/queries/user';
import { Text } from '@mofid-securities/uikit';
import { configSentry } from './utils/configSentry';
import type { IUser } from '@/services/apis/user/types';
import { cacheableQueryOptions } from '@/services/utils';
import { MainSplashScreen } from '@/components/Splash';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useCheckAutoLoginBottomSheetExceptions } from './hooks/useCheckAutoLoginBottomSheetExceptions';
import {
  TUseSetUserStatusData,
  useGetUserStatus,
  useOpenUserStatusBottomSheet,
  useSetUserStatus,
} from './hooks';
import { handleUniqueDeviceId } from '@/utils/device';
import { setUserIdToHeapAnalytics } from '@/utils/heap-analytics';

interface IUserStatusContainerProps extends PropsWithChildren {
  showBottomSheet?: boolean;
}

const UserStatusContainer: FC<IUserStatusContainerProps> = ({
  showBottomSheet,
  children,
}) => {
  const setUserStatus = useSetUserStatus();
  const { status } = useGetUserStatus();
  const { isExceptionPath } = useCheckAutoLoginBottomSheetExceptions();

  const openUserStatusBottomSheet = useOpenUserStatusBottomSheet();
  const isShownBottomSheetRef = useRef<boolean>(false);

  const {
    data: user,
    isLoading,
    isError,
  } = useGetUser({
    ...cacheableQueryOptions,
    retry: false,
    notifyOnChangeProps: ['data', 'isLoading', 'error'],
    onSuccess: (user: IUser) => {
      handleUniqueDeviceId();
      configSentry(user);
      setUserIdToHeapAnalytics(user?.hashedPk);
    },
  });

  useLayoutEffect(() => {
    if (isLoading) return;

    const status: TUseSetUserStatusData['status'] = !user
      ? 'ANONYMOUS'
      : user?.isNeedRegistered === true
        ? 'GUEST'
        : 'VERIFIED';

    setUserStatus(status);
  }, [isLoading]);

  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    const shouldOpenUserStatusBottomSheet =
      showBottomSheet &&
      !isShownBottomSheetRef.current &&
      !isExceptionPath &&
      isOnline &&
      !isError; // * Prevent the bottom sheet from opening if the user/me service encounters an error.

    if (shouldOpenUserStatusBottomSheet) {
      openUserStatusBottomSheet({
        delay: 1000,
        onOpen: () => {
          isShownBottomSheetRef.current = true;
        },
        children:
          status === 'ANONYMOUS' ? (
            <div className="mt-1 flex flex-col gap-y-3">
              <Text color="blue" size="md" weight="medium">
                ورود به حساب کاربری
              </Text>
              <Text color="gray-90" size="sm">
                جهت دسترسی به امکانات مفید اپ، ‌به حساب کاربری خود وارد شوید و
                یا حساب کاربری جدید ایجاد کنید.
              </Text>
            </div>
          ) : undefined,
      });
    }
  }, [showBottomSheet, status]);

  return !status ? <MainSplashScreen /> : <>{children}</>;
};

export default UserStatusContainer;
